import { defaultFirmaKlienterklaeringColumns } from "../../../../../config/firmaConfig";
import { firmaKlientkontoerklaeringForm } from "../../../../../config/form/firma/firmaKlientkontoerklaeringConfig";
import useActiveColumns from "../../../../../hooks/useActiveColumns";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import { ModalProvider } from "../../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../../Table/TableBasic";
import IngenRegistreringer from "../../../person/id/IngenRegistreringer";
import OpretNyButtonBlock from "../../../person/id/OpretNyButtonBlock";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import ButtonDownload from "../../../../ButtonDownload";
import ActiveIcon from "../../../../icons/ActiveIcon";
import DocumentModal from "../../../../Modal/modals/DocumentModal";
import DocumentIcon from "../../../../icons/DocumentIcon";
import { FirmaDto } from "../../../../../interfaces/schema";
import { SortBy } from "../../../../../interfaces/table";
import { FirmaKlientErklaeringField } from "../../../../../interfaces/firma";
import useLocalStorageState from "use-local-storage-state";
import { useContext } from "react";
import { UserQueryContext } from "../../../../../context/UserContext";
import TLinkButton from "../../../../popover/TLinkButton";
import { orderBy } from "../../../../../util/orderBy";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";

export default function FirmaKlientKontoErklaering({
  firma,
  firmaKlienterklaering,
  invalidateData,
}: {
  firma: FirmaDto;
  firmaKlienterklaering: FirmaDto["firmaKlienterklaering"];
  invalidateData: InvalidateDataFunction<any>;
}) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "TilsynWrite");
  const [
    firmaKlienterklaeringerColumns,
    setFirmaKlienterklaeringActiveColumns,
  ] = useActiveColumns(
    "firma-firmaklienterklaering-active-columns",
    defaultFirmaKlienterklaeringColumns,
  );

  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "firmaKlientErklaeringSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );

  function downloadHandler() {
    if (firmaKlienterklaering) {
      tableToXLSX(firmaKlienterklaeringerColumns, firmaKlienterklaering, {
        fileName: `klientkontoerklæring-for-${firma.firmaNr}`,
        additionalData: {
          firmaNr: firma.firmaNr,
        },
      });
    } else {
      console.error("Ingen klienterklæring");
    }
  }

  if (firmaKlienterklaering?.length === 0) {
    return (
      <IngenRegistreringer
        invalidateData={invalidateData}
        editConfig={
          canWrite ? firmaKlientkontoerklaeringForm(firma.id) : undefined
        }
      />
    );
  }
  return (
    <>
      <ButtonDownload onClick={downloadHandler} />
      <ModalProvider>
        <TableRoot>
          <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
            <>
              <th className="indicator"></th>
              {getHeaderActiveColumns(firmaKlienterklaeringerColumns, {
                defaultSortBy,
                setSortBy,
                sortBy,
              })}
              <th scope="col">Link</th>
              <th scope="col">Erklæring</th>
            </>
          </TableHead>
          <TableBody>
            {orderBy(
              firmaKlienterklaering,
              sortBy.map((s) => s.field),
              sortBy.map((s) => s.direction),
            ).map((klienterklaering, i) => {
              const editConfig = firmaKlientkontoerklaeringForm(
                firma.id,
                klienterklaering.id,
                klienterklaering,
              );
              return (
                <TableRow
                  key={i}
                  invalidateData={invalidateData}
                  editConfig={canWrite ? editConfig : undefined}
                >
                  <td
                    className={`indicator${
                      new Date(klienterklaering.frist) > new Date()
                        ? " has-effect"
                        : ""
                    }`}
                  >
                    <ActiveIcon />
                  </td>
                  <>
                    {firmaKlienterklaeringerColumns
                      .filter((col) => col.active)
                      .map((col) => {
                        return (
                          <TD
                            key={col.field}
                            col={col}
                            rowData={klienterklaering}
                          />
                        );
                      })}
                  </>
                  <td>
                    {!klienterklaering.erklModtaget &&
                      klienterklaering.klienterklaeringStatusNavigation?.nr ===
                        "FRIST_ORDINAER" && (
                        <TLinkButton
                          id={klienterklaering.id}
                          erklaeringtype="klienterklaering"
                        />
                      )}
                  </td>
                  <td>
                    {editConfig.getDocument && (
                      <DocumentModal
                        getDocument={editConfig.getDocument}
                        downloadLink={editConfig.downloadLink}
                        title={
                          <>
                            <DocumentIcon height={20} />
                            Klientkontoerklæring
                          </>
                        }
                        buttonContents={[
                          <DocumentIcon key={1} />,
                          <span key={2}>Vis erklæring</span>,
                        ]}
                        contentProps={{
                          className: "c-document c-document--klienterklaering",
                        }}
                      />
                    )}
                  </td>
                </TableRow>
              );
            })}
          </TableBody>
        </TableRoot>
        <ColumnEditorModal
          defaultColumns={defaultFirmaKlienterklaeringColumns}
          columns={firmaKlienterklaeringerColumns}
          setActiveColumns={setFirmaKlienterklaeringActiveColumns}
        />
        <OpretNyButtonBlock
          invalidateData={invalidateData}
          editConfig={
            canWrite ? firmaKlientkontoerklaeringForm(firma.id) : undefined
          }
        />
      </ModalProvider>
    </>
  );
}

const defaultSortBy: SortBy<FirmaKlientErklaeringField>[] = [
  {
    direction: "desc",
    field: "erklAar",
  },
  {
    direction: "desc",
    field: "erklModtaget",
  },
];
